<template>
  <div v-if="isLoading" class="flex flex-col items-center justify-center flex-1 px-4 py-10 sm:px-14">
    <span class="i-heroicons-arrow-path text-slate-500 size-8 animate-spin" />
    <p class="text-sm text-center text-slate-500 pt-4">
      Loading...
    </p>
  </div>

  <div v-if="!isLoading && !isError">
    <div class="my-4">
      <EmailSettingsForm
        :model="emailDefaultFromAddress"
      />
    </div>

    <hr class="my-4 border-slate-300" />

    <div class="flex sm:flex-row gap-4 mb-4 justify-between">
      <h4 class="mb-2">Templates</h4>

      <BaseButton
        variant="primary"
        text="Add New Template"
        @click="showModal = true"
      />
    </div>

    <div class="mt-4">
      <AppTabsVertical
        :items="emailTemplatesTabs"
        orientation="vertical"
      >
        <template
          v-for="(emailTemplatesTab, index) in emailTemplatesTabs"
          :key="`emailTemplate-${index}`"
          #[emailTemplatesTab.slot]
        >
          <div class="px-4 sm:px-6">
            <h5 class="font-bold leading-6 text-lg">{{ emailTemplatesTab.label }}</h5>

            <hr class="my-4 border-slate-300" />

            <EmailTemplateForm
              :template="emailTemplatesTab.template"
              @update="(template) => emailTemplatesTab.template = template"
            />
          </div>
        </template>
      </AppTabsVertical>
    </div>

    <AppModal
      v-model="showModal"
      title="Add New Email Template"
      width="w-[95%] sm:w-1/2"
    >
      <template #body>
        <div class="mt-4">
          <EmailTemplateForm
            :is-updating="false"
            @update="(template) => addNewTemplate(template)"
          />
        </div>
      </template>
    </AppModal>
  </div>

  <EmptyState v-else-if="!isLoading && isError">
    <template #icon>
      <span class="i-heroicons-exclamation-circle text-red-500 size-8"></span>
    </template>
    <template #title>
      <h2 class="text-lg font-semibold text-center text-slate-900">Error</h2>
    </template>
    <template #description>
      <p class="text-sm text-center text-slate-500 pt-4">
        An error occurred while trying to load the email settings
      </p>
    </template>
  </EmptyState>
</template>

<script lang="ts" setup>
import EmailSettingsForm from '../components/EmailSettingsForm.vue'
import EmailTemplateForm from '../components/EmailTemplateForm.vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import SettingService from '@/services/SettingService'
import EmptyState from '@/components/app/EmptyState.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import AppTabsVertical from '@/components/app/AppTabsVertical.vue'
import { computed, ref, watch } from 'vue'
import EmailService, { EmailTemplate } from '@/services/EmailService'
import AppModal from '@/components/app/AppModal.vue'

const showModal = ref(false)
const emailService = new EmailService()

const queryClient = useQueryClient()

const { data: emailTemplates, isLoading: emailTemplatesLoading, isError: emailTemplatesErr } = useQuery({
  queryKey: ['emailTemplates'],
  queryFn: async () => {
    return await emailService.all() as EmailTemplate[]
  }
})

const emailTemplatesTabs = ref([])

watch(emailTemplatesLoading, (loading) => {
  if (!loading && !emailTemplatesErr.value) {
    emailTemplatesTabs.value = emailTemplates.value.map((template) => {
      return {
        label: template.label,
        name: template.name,
        slot: `emailTemplate-${template.name}`,
        template: template,
      }
    })
  }
}, { immediate: true })

const { data: emailDefaultFromAddress, isLoading: emailDefaultAddrLoading, isError: emailDefaultAddrErr } = useQuery({
  queryKey: ['settings', 'emailDefaultFromAddress'],
  queryFn: async () => {
    const settings = await new SettingService().find('emailDefaultFromAddress')
    return settings.value
  },
})


const isLoading = computed(() => emailDefaultAddrLoading.value || emailTemplatesLoading.value)
const isError = computed(() => emailDefaultAddrErr.value || emailTemplatesErr.value)


const addNewTemplate = (template: EmailTemplate) => {
  emailTemplatesTabs.value.push({
    label: template.label,
    name: template.name,
    slot: `emailTemplate-${template.name}`,
    template: template,
  })

  queryClient.setQueryData(['emailTemplates'], [
    ...emailTemplates.value,
    template,
  ])
  showModal.value = false
}
</script>
